import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users as UsersIcon, Rocket, Loader2, Check, Info } from "lucide-react";
import { apiClient, billingApi } from "../lib/api";
import { Button } from "./ui/button";
import EmbeddedStripeCheckoutModal from "./payments/EmbeddedStripeCheckoutModal";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/** Ship 1 of the "kill seat count" refactor. Renders the trial's unactivated
 *  users with checkboxes, a Monthly/Annual toggle, live prorated total, and
 *  an emerald "Activate now" button that opens Stripe **embedded** checkout. */
export default function ActivateUsersCard() {
  const [status, setStatus] = useState(null);
  const [term, setTerm] = useState("monthly");
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [checkout, setCheckout] = useState(null); // {clientSecret, sessionId}

  const load = async () => {
    try {
      const { data } = await apiClient.get("/billing/activation-status");
      setStatus(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load activation status");
    }
  };
  useEffect(() => { load(); }, []);

  const prorate = status?.prorate?.[term];
  const perUser = prorate?.perUserProrated || 0;
  const total = useMemo(() => Number((perUser * selectedIds.length).toFixed(2)), [perUser, selectedIds]);
  const unactivated = status?.unactivatedUsers || [];
  const activated = status?.activatedUsers || [];
  const isFirstOfTerm = !!prorate?.anchorNew;

  const toggle = (id) => {
    setSelectedIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const toggleAll = () => {
    setSelectedIds(selectedIds.length === unactivated.length ? [] : unactivated.map((u) => u.id));
  };

  const activate = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      const resp = await billingApi.activateUsers(selectedIds, term, window.location.origin, { embedded: true });
      if (resp?.client_secret) {
        setCheckout({ clientSecret: resp.client_secret, sessionId: resp.session_id, amount: resp.amount });
      } else if (resp?.url) {
        // Legacy hosted fallback
        window.location.href = resp.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
    } finally {
      setBusy(false);
    }
  };

  const onPaymentComplete = useCallback(async () => {
    const sid = checkout?.sessionId;
    if (!sid) { setCheckout(null); return; }
    for (let i = 0; i < 6; i += 1) {
      const s = await billingApi.status(sid).catch(() => null);
      if (s?.payment_status === "paid") {
        toast.success("Payment received — users activated!");
        setCheckout(null);
        setSelectedIds([]);
        await load();
        return;
      }
      await new Promise((r) => setTimeout(r, 1500));
    }
    toast.message("Payment still processing. Refresh in a moment.");
    setCheckout(null);
  }, [checkout]);

  if (!status) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mb-6 text-center" data-testid="activate-users-loading">
        <Loader2 className="h-5 w-5 text-blue-500 animate-spin inline" />
      </div>
    );
  }

  return (
    <>
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6" data-testid="activate-users-card">
      <div className="px-6 py-4 border-b border-slate-100">
        <div className="text-[15px] font-bold text-slate-900 flex items-center gap-2">
          <UsersIcon className="h-4 w-4 text-blue-600" /> Activate users
        </div>
        <div className="text-[12.5px] text-slate-500 mt-0.5">
          Pick which teammates should stay on after your trial. Each activation is
          prorated to today; renewals fall on the term&apos;s anchor date. You can mix
          Monthly and Annual users freely.
        </div>
      </div>

      {/* Term toggle */}
      <div className="px-6 pt-4 flex items-center gap-2 text-[12.5px]">
        <span className="text-slate-500">Billing term:</span>
        {["monthly", "annual"].map((t) => {
          const p = status.prorate?.[t];
          return (
            <button
              key={t}
              onClick={() => setTerm(t)}
              data-testid={`activate-term-${t}`}
              className={`h-8 px-3 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                term === t
                  ? "border-blue-600 bg-blue-600 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              {t === "monthly" ? "Monthly" : "Annual"} · {money(p?.perUserProrated || 0)}/user
            </button>
          );
        })}
        <div className="ml-auto text-[11.5px] text-slate-400 hidden md:block">
          {isFirstOfTerm
            ? `First ${term} activation sets your ${term} renewal date.`
            : `Renews ${new Date(prorate?.periodEnd).toLocaleDateString()} · ${prorate?.daysLeft} day${prorate?.daysLeft === 1 ? "" : "s"} left`}
        </div>
      </div>

      {/* Unactivated list */}
      <div className="px-6 pt-4">
        {unactivated.length === 0 ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-4 py-4 text-[13px] text-emerald-800 flex items-start gap-2" data-testid="activate-users-empty">
            <Check className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />
            <div>
              <div className="font-semibold">Everyone&apos;s activated.</div>
              <div className="text-[12px] mt-0.5 text-emerald-700/80">
                {activated.length} paid user{activated.length === 1 ? "" : "s"} on this workspace.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <button
                onClick={toggleAll}
                data-testid="activate-toggle-all"
                className="text-[12px] font-semibold text-blue-600 hover:text-blue-700"
              >
                {selectedIds.length === unactivated.length ? "Clear all" : `Select all ${unactivated.length}`}
              </button>
              <div className="text-[12px] text-slate-500">
                {selectedIds.length} selected · {activated.length} already activated
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 overflow-hidden">
              {unactivated.map((u) => {
                const on = selectedIds.includes(u.id);
                return (
                  <label
                    key={u.id}
                    data-testid={`activate-row-${u.id}`}
                    className={`flex items-center gap-3 px-3.5 py-2.5 cursor-pointer transition-colors ${
                      on ? "bg-blue-50/50" : "hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(u.id)}
                      data-testid={`activate-check-${u.id}`}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-slate-800 truncate">{u.name || u.email}</div>
                      <div className="text-[11.5px] text-slate-500 truncate font-mono">{u.email}</div>
                    </div>
                    <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">{u.role}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Total + activate CTA */}
      {unactivated.length > 0 && (
        <div className="px-6 py-4 mt-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11.5px] uppercase tracking-wider font-semibold text-slate-400">Total due today</div>
            <div className="text-[22px] font-bold text-slate-900 leading-tight" data-testid="activate-total">
              {money(total)}
            </div>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {selectedIds.length || 0} × {money(perUser)} ({term})
              {prorate && !isFirstOfTerm && ` · prorated`}
            </div>
          </div>
          <Button
            onClick={activate}
            disabled={busy || selectedIds.length === 0}
            data-testid="activate-users-btn"
            className="h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-[14px] font-semibold disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Rocket className="h-4 w-4 mr-1.5" />}
            Activate {selectedIds.length || ""} user{selectedIds.length === 1 ? "" : "s"} · {money(total)}
          </Button>
        </div>
      )}

      {/* Info footer */}
      <div className="px-6 py-2.5 text-[11.5px] text-slate-500 flex items-start gap-1.5 border-t border-slate-100">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
        <span>
          After payment, users are activated instantly. Monthly and Annual renewals
          are billed on their own anchor dates — you can freely mix terms per user.
        </span>
      </div>
    </div>
    {checkout && (
      <EmbeddedStripeCheckoutModal
        clientSecret={checkout.clientSecret}
        title={`Activate ${selectedIds.length} user${selectedIds.length === 1 ? "" : "s"}`}
        subtitle={`${term === "annual" ? "Annual" : "Monthly"} · ${money(checkout.amount || total)}`}
        onComplete={onPaymentComplete}
        onClose={() => setCheckout(null)}
        testid="activate-users-embedded-checkout"
      />
    )}
    </>
  );
}
